import { useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Button,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';

export type PickupTypeFilter = 'all' | 'car' | 'foot';
export type WaitTimeFilter = 'all' | '10min' | '20min';

interface DismissalFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  pickupTypeFilter: PickupTypeFilter;
  onPickupTypeChange: (filter: PickupTypeFilter) => void;
  waitTimeFilter: WaitTimeFilter;
  onWaitTimeChange: (filter: WaitTimeFilter) => void;
  gradeFilter: string;
  onGradeFilterChange: (grade: string) => void;
  availableGrades: string[];
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

export default function DismissalFilters({
  searchQuery,
  onSearchChange,
  pickupTypeFilter,
  onPickupTypeChange,
  waitTimeFilter,
  onWaitTimeChange,
  gradeFilter,
  onGradeFilterChange,
  availableGrades,
  onClearAll,
  hasActiveFilters,
}: DismissalFiltersProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = useState(true);

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or grade..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  edge="end"
                  aria-label="clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            bgcolor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          }}
        />

        {/* Filter Toggle Button (Mobile) */}
        {isMobile && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        )}

        {/* Filter Chips */}
        <Collapse in={!isMobile || showFilters}>
          <Stack spacing={2}>
            {/* Pickup Type Filter */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                Pickup Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label="All"
                  onClick={() => onPickupTypeChange('all')}
                  color={pickupTypeFilter === 'all' ? 'primary' : 'default'}
                  variant={pickupTypeFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<DirectionsCarIcon sx={{ fontSize: 16 }} />}
                  label="Car Riders"
                  onClick={() => onPickupTypeChange('car')}
                  color={pickupTypeFilter === 'car' ? 'primary' : 'default'}
                  variant={pickupTypeFilter === 'car' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<DirectionsWalkIcon sx={{ fontSize: 16 }} />}
                  label="Walkers"
                  onClick={() => onPickupTypeChange('foot')}
                  color={pickupTypeFilter === 'foot' ? 'primary' : 'default'}
                  variant={pickupTypeFilter === 'foot' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Stack>
            </Box>

            {/* Wait Time Filter */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                Wait Time
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label="All"
                  onClick={() => onWaitTimeChange('all')}
                  color={waitTimeFilter === 'all' ? 'primary' : 'default'}
                  variant={waitTimeFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label="Waiting > 10 min"
                  onClick={() => onWaitTimeChange('10min')}
                  color={waitTimeFilter === '10min' ? 'warning' : 'default'}
                  variant={waitTimeFilter === '10min' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label="Waiting > 20 min"
                  onClick={() => onWaitTimeChange('20min')}
                  color={waitTimeFilter === '20min' ? 'error' : 'default'}
                  variant={waitTimeFilter === '20min' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Stack>
            </Box>

            {/* Grade Filter */}
            {availableGrades.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                  Grade Level
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="All Grades"
                    onClick={() => onGradeFilterChange('')}
                    color={gradeFilter === '' ? 'primary' : 'default'}
                    variant={gradeFilter === '' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  {availableGrades.map((grade) => (
                    <Chip
                      key={grade}
                      icon={<SchoolIcon sx={{ fontSize: 16 }} />}
                      label={`Grade ${grade}`}
                      onClick={() => onGradeFilterChange(grade)}
                      color={gradeFilter === grade ? 'primary' : 'default'}
                      variant={gradeFilter === grade ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Clear All Filters */}
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={onClearAll}
                  sx={{ textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
